import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Form, Input, InputNumber, Modal, Popconfirm, Progress, Select, Space, Switch, Table, Tag, Tooltip, Typography, message } from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import evmAdminService from '../../../shared/services/evmAdminService';

const { Text } = Typography;

const ROLES = [{ value: 'vm', label: '云盘' }, { value: 'backup', label: '备份' }];
const TYPES = [
    { value: 'local', label: '本地目录 (qcow2)' },
    { value: 'nfs', label: 'NFS (共享, qcow2)' },
    { value: 'lvmthin', label: 'LVM-thin (本地)' },
    { value: 'lvm', label: 'LVM (本地, 厚置备)' },
    { value: 'zfs', label: 'ZFS (本地, zvol)' },
    { value: 'iscsi', label: 'iSCSI (共享 LVM)' },
    { value: 'ceph', label: 'Ceph RBD (共享)' },
];
const TYPE_TAG = { local: '本地', nfs: 'NFS', lvmthin: 'LVM-thin', lvm: 'LVM', zfs: 'ZFS', iscsi: 'iSCSI', ceph: 'RBD' };
const isBlock = (t) => t === 'lvm' || t === 'lvmthin' || t === 'iscsi' || t === 'zfs';
const gb = (v) => (v === null || v === undefined ? '-' : `${Math.round(v)} GB`);

// Per-type fields (StorageTypes::validateConfig); the agent attaches the pool and reports it.
const PoolConfigFields = ({ type, locked }) => {
    if (type === 'ceph') return <Text type="secondary">Ceph 存储池在「Ceph 集群」页面接入。</Text>;
    const path = (placeholder, tip) => (
        <Form.Item name="path" label="路径" rules={[{ required: true }]} tooltip={tip}>
            <Input placeholder={placeholder} disabled={locked} />
        </Form.Item>
    );
    if (type === 'nfs') return (
        <>
            {path('/mnt/yeap-nfs1', '各节点的挂载点；同一 NFS 在所有节点用同一路径')}
            <Space size="middle" wrap>
                <Form.Item name={['config', 'server']} label="NFS 服务器" rules={[{ required: true }]}><Input placeholder="10.0.0.5" disabled={locked} style={{ width: 160 }} /></Form.Item>
                <Form.Item name={['config', 'export']} label="导出路径" rules={[{ required: true }]}><Input placeholder="/srv/evm" disabled={locked} style={{ width: 160 }} /></Form.Item>
                <Form.Item name={['config', 'version']} label="版本"><Select allowClear placeholder="4.2" style={{ width: 90 }} options={['4.2', '4.1', '4', '3'].map(v => ({ value: v, label: v }))} /></Form.Item>
            </Space>
            <Form.Item name={['config', 'options']} label="附加挂载选项" tooltip="已默认 hard,timeo=600,noatime"><Input placeholder="nconnect=4" /></Form.Item>
        </>
    );
    if (type === 'lvm' || type === 'lvmthin') return (
        <Space size="middle" wrap>
            <Form.Item name={['config', 'vg']} label="卷组 (VG)" rules={[{ required: true }]} tooltip="节点上已建好的 VG"><Input placeholder="vg_evm" disabled={locked} style={{ width: 150 }} /></Form.Item>
            {type === 'lvmthin' && <Form.Item name={['config', 'thinpool']} label="精简池" rules={[{ required: true }]}><Input placeholder="thinpool" disabled={locked} style={{ width: 150 }} /></Form.Item>}
            {type === 'lvmthin' && <Form.Item name={['config', 'create']} label="不存在时创建" valuePropName="checked" tooltip="用 VG 剩余空间的 95% 建精简池"><Switch disabled={locked} /></Form.Item>}
        </Space>
    );
    if (type === 'zfs') return (
        <Space size="middle" wrap>
            <Form.Item name={['config', 'dataset']} label="数据集" rules={[{ required: true }]} tooltip="例如 tank/evm；zvol 建在其下"><Input placeholder="tank/evm" disabled={locked} style={{ width: 200 }} /></Form.Item>
            <Form.Item name={['config', 'create']} label="不存在时创建" valuePropName="checked" tooltip="池须已存在"><Switch disabled={locked} /></Form.Item>
        </Space>
    );
    if (type === 'iscsi') return (
        <>
            <Space size="middle" wrap>
                <Form.Item name={['config', 'portal']} label="门户" rules={[{ required: true }]}><Input placeholder="10.0.0.6:3260" disabled={locked} style={{ width: 160 }} /></Form.Item>
                <Form.Item name={['config', 'lun']} label="LUN"><InputNumber min={0} max={16383} placeholder="0" disabled={locked} /></Form.Item>
                <Form.Item name={['config', 'vg']} label="卷组 (VG)" rules={[{ required: true }]}><Input placeholder="vg_san1" disabled={locked} style={{ width: 140 }} /></Form.Item>
            </Space>
            <Form.Item name={['config', 'target']} label="目标 IQN" rules={[{ required: true }]}><Input placeholder="iqn.2026-10.com.example:evm" disabled={locked} /></Form.Item>
            <Space size="middle" wrap>
                <Form.Item name={['config', 'chap_user']} label="CHAP 用户名" tooltip="目标不要求认证时留空"><Input autoComplete="off" style={{ width: 160 }} /></Form.Item>
                <Form.Item noStyle shouldUpdate>
                    {({ getFieldValue }) => (
                        <Form.Item name={['config', 'chap_pass']} label="CHAP 密码">
                            <Input.Password autoComplete="new-password" style={{ width: 180 }}
                                placeholder={getFieldValue(['config', 'chap_pass_set']) ? '已设置，留空不改' : '8-255 个字符'} />
                        </Form.Item>
                    )}
                </Form.Item>
            </Space>
            <Form.Item name={['config', 'init']} label="LUN 为空时初始化 VG" valuePropName="checked" tooltip="仅在 LUN 无任何签名时创建；其它节点直接接入"><Switch disabled={locked} /></Form.Item>
        </>
    );
    return path('/home/kvm', '节点上的目录；节点上报后才能启用');
};

const StoragePoolManagement = () => {
    const [data, setData] = useState({ pools: [], nodes: [] });
    const [nodeId, setNodeId] = useState(undefined);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null); // {} = new
    const [form] = Form.useForm();

    const load = useCallback(async () => {
        setLoading(true);
        const res = await evmAdminService.getStoragePools(nodeId ? { node_id: nodeId } : {});
        setLoading(false);
        if (res.status === 'success') setData(res.data || { pools: [], nodes: [] });
        else message.error(res.msg || '加载失败');
    }, [nodeId]);

    useEffect(() => { load(); }, [load]);

    const nodeName = useMemo(() => Object.fromEntries((data.nodes || []).map(n => [n.id, n.name])), [data.nodes]);

    const act = async (p, ok = '已保存') => {
        const res = await p;
        if (res.status === 'success') { message.success(res.msg || ok); load(); return true; }
        message.error(res.msg || '操作失败');
        return false;
    };

    const openEdit = (row) => {
        setEditing(row || {});
        form.setFieldsValue(row
            ? { ...row, size: row.size || null, config: row.config || {} }
            : { node_id: nodeId, type: 'local', role: 'vm', sort: 1, size: null, config: {} });
    };

    const submit = async () => {
        const v = await form.validateFields();
        const body = { ...v, size: v.size ? v.size : 'auto' };
        if (v.type === 'ceph') delete body.config; // Ceph pools come from the Ceph cluster page
        else {
            const { chap_pass_set, ...cfg } = v.config || {}; // eslint-disable-line no-unused-vars
            body.config = JSON.stringify(cfg);
        }
        if (isBlock(v.type)) delete body.path; // derived from the VG / dataset
        const ok = editing.id
            ? await act(evmAdminService.updateStoragePool(editing.id, body))
            : await act(evmAdminService.createStoragePool(body), '已创建');
        if (ok) setEditing(null);
    };

    const columns = [
        { title: '节点', dataIndex: 'node_id', width: 130, render: (id) => nodeName[id] || `#${id}` },
        { title: '名称', dataIndex: 'name', width: 140 },
        {
            title: '类型', dataIndex: 'type', width: 110,
            render: (t, r) => <Space size={2}><Tag>{TYPE_TAG[t] || t}</Tag>{r.shared && <Tooltip title="多节点共享，迁移不拷盘"><Tag color="green">共享</Tag></Tooltip>}</Space>,
        },
        {
            title: '路径', dataIndex: 'path',
            render: (p, r) => (
                <Space direction="vertical" size={0}>
                    <Text code>{p}</Text>
                    {r.type === 'nfs' && r.config?.server && <Text type="secondary">{r.config.server}:{r.config.export}</Text>}
                    {r.type === 'iscsi' && r.config?.target && <Text type="secondary">{r.config.portal} {r.config.target} LUN {r.config.lun || 0}</Text>}
                    {r.report_state === 'error' && <Text type="danger">{r.report_error}</Text>}
                </Space>
            ),
        },
        { title: '用途', dataIndex: 'role', width: 70, render: (r) => <Tag color={r === 'backup' ? 'purple' : 'blue'}>{r === 'backup' ? '备份' : '云盘'}</Tag> },
        {
            title: <Tooltip title="已售 = 云盘容量合计；上限 = 上报总量 × 超售比（或手动上限）">已售 / 上限</Tooltip>,
            width: 200,
            render: (_, r) => r.role === 'backup' ? '-' : (
                <div>
                    <Progress size="small" percent={r.limit_gb ? Math.min(100, Math.round(r.committed_gb / r.limit_gb * 100)) : 0} showInfo={false} />
                    <Text type="secondary">{gb(r.committed_gb)} / {r.limit_gb === null ? '不限' : gb(r.limit_gb)}</Text>
                </div>
            ),
        },
        {
            title: '物理 已用 / 总量',
            width: 190,
            render: (_, r) => r.total_gb ? (
                <div>
                    <Progress size="small" percent={Math.round(r.used_pct || 0)} status={r.used_pct >= data.place_max_pct ? 'exception' : 'normal'} />
                    <Text type={r.fresh ? 'secondary' : 'warning'}>{gb(r.used_gb)} / {gb(r.total_gb)}{r.fresh ? '' : '（过期）'}</Text>
                </div>
            ) : <Text type="warning">未上报</Text>,
        },
        { title: '手动上限', dataIndex: 'size', width: 90, render: (s) => (s ? gb(s) : '自动') },
        {
            title: '启用', dataIndex: 'status', width: 80,
            render: (s, r) => (
                <Tooltip title={!r.usable && s === 'enable' ? r.reason : ''}>
                    <Switch size="small" checked={s === 'enable'}
                        onChange={(on) => act(evmAdminService.updateStoragePool(r.id, { status: on ? 'enable' : 'disable' }))} />
                </Tooltip>
            ),
        },
        {
            title: '', width: 90, render: (_, r) => (
                <Space size={4}>
                    <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
                    <Popconfirm title="删除该存储池？" onConfirm={() => act(evmAdminService.deleteStoragePool(r.id), '已删除')} disabled={r.disks > 0}>
                        <Button size="small" danger icon={<DeleteOutlined />} disabled={r.disks > 0} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    const nodeColumns = [
        { title: '节点', dataIndex: 'name' },
        { title: 'Agent', dataIndex: 'agent_version', render: (v) => v || <Text type="secondary">-</Text> },
        {
            title: <Tooltip title="1 = 不超售；活动机可调高">磁盘超售比</Tooltip>, dataIndex: 'disk_oversell_ratio', width: 160,
            render: (v, n) => (
                <InputNumber size="small" min={1} max={10} step={0.5} defaultValue={v} disabled={!data.ratio_supported}
                    onBlur={(e) => { const x = parseFloat(e.target.value); if (x && x !== v) act(evmAdminService.setNodeDiskRatio(n.id, x)); }} />
            ),
        },
    ];

    return (
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Card
                title="存储池"
                extra={(
                    <Space>
                        <Select allowClear placeholder="全部节点" style={{ width: 180 }} value={nodeId} onChange={setNodeId}
                            options={(data.nodes || []).map(n => ({ value: n.id, label: n.name }))} />
                        <Button icon={<ReloadOutlined />} onClick={load} />
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => openEdit(null)}>新建</Button>
                    </Space>
                )}
            >
                <Table rowKey="id" size="small" loading={loading} columns={columns} dataSource={data.pools} pagination={false} scroll={{ x: 1100 }} />
            </Card>
            <Card title="节点磁盘超售" size="small">
                <Table rowKey="id" size="small" columns={nodeColumns}
                    dataSource={(data.nodes || []).filter(n => !nodeId || n.id === nodeId)} pagination={{ pageSize: 10, hideOnSinglePage: true }} />
            </Card>

            <Modal title={editing?.id ? '编辑存储池' : '新建存储池'} open={!!editing} onOk={submit} onCancel={() => setEditing(null)} destroyOnClose>
                <Form form={form} layout="vertical">
                    {!editing?.id && (
                        <Form.Item name="node_id" label="节点" rules={[{ required: true }]}>
                            <Select options={(data.nodes || []).map(n => ({ value: n.id, label: n.name }))} />
                        </Form.Item>
                    )}
                    <Form.Item name="name" label="名称" rules={[{ required: true }]}><Input /></Form.Item>
                    <Form.Item name="type" label="类型" tooltip="快照：本地 / NFS / LVM-thin / ZFS / RBD（ZFS 只能回滚到最新快照）；LVM 厚置备与 iSCSI 不支持快照">
                        <Select options={TYPES} disabled={editing?.disks > 0} />
                    </Form.Item>
                    <Form.Item noStyle shouldUpdate={(a, b) => a.type !== b.type}>
                        {({ getFieldValue }) => <PoolConfigFields type={getFieldValue('type')} locked={editing?.disks > 0} />}
                    </Form.Item>
                    <Space size="large">
                        <Form.Item name="role" label="用途"><Select style={{ width: 110 }} options={ROLES} disabled={editing?.disks > 0 || !data.role_supported} /></Form.Item>
                        <Form.Item name="size" label="手动上限 (GB)" tooltip="留空 = 自动（上报容量 × 超售比）">
                            <InputNumber min={1} placeholder="自动" style={{ width: 130 }} />
                        </Form.Item>
                        <Form.Item name="sort" label="排序"><InputNumber min={0} style={{ width: 80 }} /></Form.Item>
                    </Space>
                </Form>
            </Modal>
        </Space>
    );
};

export default StoragePoolManagement;
